import React, { useEffect, useState } from "react";
import { Spinner, Modal, Button } from "react-bootstrap";
import QuickQuizService, { WebProctoringData } from "../../_services/QuickQuizService";

/**
 * Webcam Proctoring screen — Node/React port of the Java WebCamProctoring.jsp
 * (popup/studentReport.do?reqCode=getWebProctoring). Renders the student's
 * proctoring evidence: profile photo, violation summary, per-capture metrics
 * (with S3 photos), the audit trail, and the test-block / blanket-block controls.
 *
 * All DynamoDB access happens server-side via /quick-quiz/:testId/web-proctoring/...
 * (the Java JSP read DynamoDB directly in the browser with embedded AWS creds).
 */
interface WebProctoringPanelProps {
  testId: number;
  enquiryId: number;
  onBack: () => void;
}

/** Pass/Fail/-- colour chip palette for the metrics table. */
const passFail = (v: string): { bg: string; fg: string; bd: string } => {
  if (v === "Pass") return { bg: "#dcfce7", fg: "#15803d", bd: "#bbf7d0" };
  if (v === "Fail") return { bg: "#fee2e2", fg: "#b91c1c", bd: "#fecaca" };
  return { bg: "#f1f5f9", fg: "#94a3b8", bd: "#e2e8f0" };
};

const WebProctoringPanel: React.FC<WebProctoringPanelProps> = ({ testId, enquiryId, onBack }) => {
  const [data, setData] = useState<WebProctoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [audit, setAudit] = useState<any>(null);
  const [auditLoading, setAuditLoading] = useState(true);

  // Local mirror of block state so the toggles reflect changes immediately.
  const [blockTest, setBlockTest] = useState(false);
  const [blanketFlag, setBlanketFlag] = useState("");
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState<{ show: boolean; message: string; variant: "success" | "danger" }>({ show: false, message: "", variant: "success" });
  const [zoom, setZoom] = useState<string | null>(null); // photo lightbox

  const load = async () => {
    setLoading(true);
    try {
      const result = await QuickQuizService.getWebProctoring(testId, enquiryId);
      setData(result);
      setBlockTest(result.blockTest);
      setBlanketFlag(result.blanketFlag);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to load proctoring data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setAuditLoading(true);
    QuickQuizService.getStudentAuditTrail(testId, enquiryId)
      .then((a) => setAudit(a))
      .catch(() => setAudit({ error: "Failed to load audit trail" }))
      .finally(() => setAuditLoading(false));
  }, [testId, enquiryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const notify = (message: string, variant: "success" | "danger") => setInfo({ show: true, message, variant });

  const handleTestBlock = async (next: boolean) => {
    setSaving(true);
    const prev = blockTest;
    setBlockTest(next);
    try {
      const r = await QuickQuizService.setTestBlock(testId, enquiryId, next);
      notify(r.message || "Test block updated", "success");
    } catch (err: any) {
      setBlockTest(prev);
      notify(err?.response?.data?.message || "Failed to update test block", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleBlanket = async (status: "BLOCK" | "ALLOW" | "") => {
    setSaving(true);
    const prev = blanketFlag;
    setBlanketFlag(status);
    try {
      const r = await QuickQuizService.setBlanketBlock(testId, enquiryId, status);
      notify(r.message || "Blanket block updated", "success");
    } catch (err: any) {
      setBlanketFlag(prev);
      notify(err?.response?.data?.message || "Failed to update blanket block", "danger");
    } finally {
      setSaving(false);
    }
  };

  const styleBlock = (
    <style>{`
      .wpx { --ink:#0f172a; --sub:#64748b; --line:#e9eef5; --brand:#6366f1; --brand2:#8b5cf6;
        animation: wpxFade .45s ease both; }
      @keyframes wpxFade { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform:none; } }
      @keyframes wpxRise { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }
      .wpx-card { background:#fff; border:1px solid var(--line); border-radius:18px;
        box-shadow:0 1px 2px rgba(15,23,42,.04), 0 18px 40px -28px rgba(15,23,42,.35);
        animation: wpxRise .5s ease both; }
      .wpx-pad { padding:20px 22px; }

      /* ── Hero ── */
      .wpx-hero { position:relative; overflow:hidden; border-radius:22px; padding:24px 26px; color:#fff;
        background:
          radial-gradient(120% 140% at 0% 0%, #6366f1 0%, rgba(99,102,241,0) 45%),
          radial-gradient(120% 160% at 100% 0%, #8b5cf6 0%, rgba(139,92,246,0) 50%),
          radial-gradient(140% 180% at 100% 120%, #0ea5e9 0%, rgba(14,165,233,0) 45%),
          linear-gradient(135deg,#1e293b 0%, #0f172a 100%);
        box-shadow:0 24px 60px -28px rgba(30,41,59,.7);
        animation: wpxRise .5s ease both; }
      .wpx-hero::after { content:""; position:absolute; inset:0; pointer-events:none;
        background-image:radial-gradient(rgba(255,255,255,.06) 1px, transparent 1px); background-size:18px 18px; }
      .wpx-hero-row { position:relative; z-index:1; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; }
      .wpx-avatar-wrap { position:relative; }
      .wpx-avatar { height:66px; width:66px; border-radius:18px; object-fit:cover; display:flex;
        align-items:center; justify-content:center; font-weight:800; font-size:1.4rem; color:#fff;
        background:var(--gradient-brand); box-shadow:0 8px 22px -8px rgba(0,0,0,.6); }
      .wpx-avatar-ring { padding:3px; border-radius:21px;
        background:linear-gradient(135deg, rgba(255,255,255,.85), rgba(255,255,255,.15)); }
      .wpx-live { position:absolute; bottom:-3px; right:-3px; width:18px; height:18px; border-radius:50%;
        background:#22c55e; border:3px solid #0f172a; box-shadow:0 0 0 0 rgba(34,197,94,.6); animation:wpxPulse 1.8s infinite; }
      @keyframes wpxPulse { 0%{box-shadow:0 0 0 0 rgba(34,197,94,.5);} 70%{box-shadow:0 0 0 9px rgba(34,197,94,0);} 100%{box-shadow:0 0 0 0 rgba(34,197,94,0);} }
      .wpx-name { font-weight:800; font-size:1.18rem; letter-spacing:.2px; }
      .wpx-meta { font-size:.8rem; opacity:.82; margin-top:2px; }
      .wpx-meta b { font-weight:700; opacity:.95; }
      .wpx-id { cursor:pointer; font-size:.72rem; font-family:ui-monospace,Menlo,monospace; color:#e2e8f0;
        background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:6px 12px; border-radius:999px;
        display:inline-flex; align-items:center; gap:7px; transition:all .15s ease; }
      .wpx-id:hover { background:rgba(255,255,255,.22); transform:translateY(-1px); }
      .wpx-hbtn { border:none; cursor:pointer; font-size:.78rem; font-weight:700; padding:8px 16px; border-radius:11px;
        transition:all .15s ease; }
      .wpx-hbtn-light { background:#fff; color:#1e293b; box-shadow:0 6px 16px -8px rgba(0,0,0,.5); }
      .wpx-hbtn-ghost { background:rgba(255,255,255,.14); color:#fff; border:1px solid rgba(255,255,255,.22); }
      .wpx-hbtn:hover { transform:translateY(-1px); filter:brightness(1.05); }
      .wpx-hstats { position:relative; z-index:1; display:flex; gap:10px; flex-wrap:wrap; margin-top:18px; }
      .wpx-hstat { background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.16); border-radius:13px;
        padding:9px 15px; min-width:108px; backdrop-filter: blur(4px); }
      .wpx-hstat .n { font-size:1.15rem; font-weight:800; line-height:1; }
      .wpx-hstat .l { font-size:.66rem; text-transform:uppercase; letter-spacing:.7px; opacity:.78; margin-top:5px; }

      /* ── Section heads ── */
      .wpx-h { display:flex; align-items:center; gap:9px; margin-bottom:14px; }
      .wpx-h .bar { width:4px; height:17px; border-radius:3px; background:linear-gradient(180deg,var(--brand),var(--brand2)); }
      .wpx-h .t { font-size:.82rem; font-weight:800; color:var(--ink); text-transform:uppercase; letter-spacing:.6px; }
      .wpx-h .c { margin-left:auto; font-size:.7rem; font-weight:700; color:var(--sub);
        background:#f1f5f9; padding:3px 9px; border-radius:999px; }

      /* ── Segmented toggle ── */
      .wpx-ctl { display:flex; flex-wrap:wrap; gap:26px; align-items:center; }
      .wpx-ctl-lbl { font-weight:700; font-size:.82rem; color:var(--ink); display:flex; align-items:center; gap:7px; }
      .wpx-seg { display:inline-flex; background:#f1f5f9; border:1px solid #e6ebf2; border-radius:12px; padding:3px; gap:2px; }
      .wpx-seg button { border:none; cursor:pointer; padding:7px 16px; font-size:.76rem; font-weight:700; border-radius:9px;
        color:#64748b; background:transparent; transition:all .18s cubic-bezier(.4,0,.2,1); }
      .wpx-seg button:disabled { cursor:default; }
      .wpx-seg button:hover:not(:disabled):not([data-on="1"]) { color:#334155; background:#fff; }
      .wpx-seg button[data-on="1"] { color:#fff; box-shadow:0 6px 14px -6px rgba(0,0,0,.45); transform:translateZ(0); }
      .wpx-tag { font-size:.78rem; font-weight:800; padding:5px 13px; border-radius:999px; display:inline-flex; align-items:center; gap:6px; }
      .wpx-dot { width:7px; height:7px; border-radius:50%; }

      /* ── Device chips ── */
      .wpx-chips { display:flex; flex-wrap:wrap; gap:9px; margin-bottom:16px; }
      .wpx-chip { font-size:.76rem; font-weight:600; padding:6px 13px; border-radius:11px; display:inline-flex; align-items:center; gap:7px; border:1px solid transparent; }

      /* ── Submit banners ── */
      .wpx-banner { font-size:.8rem; color:#334155; background:#f8fafc; border:1px solid var(--line);
        border-left:4px solid #f59e0b; border-radius:10px; padding:10px 14px; margin-bottom:8px; }
      .wpx-banner.rds { border-left-color:#0891b2; }

      /* ── Timeline ── */
      .wpx-tl { position:relative; margin-left:6px; max-height:230px; overflow-y:auto; padding-left:18px; }
      .wpx-tl::before { content:""; position:absolute; left:4px; top:4px; bottom:4px; width:2px;
        background:linear-gradient(180deg,#c7d2fe,#e9eef5); border-radius:2px; }
      .wpx-tl-item { position:relative; padding:7px 0; display:flex; justify-content:space-between; gap:14px; align-items:center; }
      .wpx-tl-item + .wpx-tl-item { border-top:1px dashed #eef2f7; }
      .wpx-tl-dot { position:absolute; left:-18px; top:50%; transform:translateY(-50%); width:11px; height:11px; border-radius:50%;
        background:#fff; border:3px solid var(--brand); box-shadow:0 0 0 3px rgba(99,102,241,.12); }
      .wpx-tl-act { font-size:.82rem; font-weight:600; color:var(--ink); }
      .wpx-tl-ts { font-size:.73rem; color:var(--sub); font-family:ui-monospace,Menlo,monospace; white-space:nowrap; }

      /* ── Violation stat cards ── */
      .wpx-vs { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:14px; }
      .wpx-vcard { position:relative; overflow:hidden; border-radius:16px; padding:16px 16px 15px; border:1px solid var(--line);
        background:#fff; transition:transform .18s ease, box-shadow .18s ease; }
      .wpx-vcard:hover { transform:translateY(-3px); box-shadow:0 16px 34px -22px rgba(15,23,42,.5); }
      .wpx-vcard .ico { width:38px; height:38px; border-radius:11px; display:flex; align-items:center; justify-content:center;
        font-size:1.15rem; margin-bottom:11px; }
      .wpx-vcard .v { font-size:1.5rem; font-weight:800; color:var(--ink); line-height:1; }
      .wpx-vcard .v.muted { color:#cbd5e1; }
      .wpx-vcard .k { font-size:.72rem; font-weight:700; color:var(--sub); text-transform:uppercase; letter-spacing:.5px; margin-top:7px; }
      .wpx-vcard .edge { position:absolute; right:-22px; top:-22px; width:80px; height:80px; border-radius:50%; opacity:.12; }

      /* ── Metrics table ── */
      .wpx-tbl-wrap { overflow-x:auto; border-radius:14px; border:1px solid var(--line); }
      .wpx-tbl { width:100%; border-collapse:separate; border-spacing:0; font-size:.82rem; }
      .wpx-tbl thead th { position:sticky; top:0; background:#f8fafc; color:#64748b; font-size:.68rem; font-weight:800;
        text-transform:uppercase; letter-spacing:.6px; padding:13px 16px; text-align:center; white-space:nowrap;
        border-bottom:2px solid var(--line); }
      .wpx-tbl tbody td { padding:12px 16px; text-align:center; vertical-align:middle; color:#334155; border-bottom:1px solid #f1f5f9; }
      .wpx-tbl tbody tr:last-child td { border-bottom:none; }
      .wpx-tbl tbody tr:nth-child(even) { background:#fcfdff; }
      .wpx-tbl tbody tr:hover { background:#f5f7ff; }
      .wpx-sno { display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:8px;
        background:#eef2ff; color:#4338ca; font-weight:800; font-size:.75rem; }
      .wpx-photo { width:140px; height:96px; object-fit:cover; border-radius:10px; cursor:zoom-in;
        border:1px solid var(--line); box-shadow:0 6px 16px -10px rgba(15,23,42,.5); transition:transform .2s ease; }
      .wpx-photo:hover { transform:scale(1.04); }
      .wpx-mchip { font-size:.74rem; font-weight:700; padding:3px 11px; border-radius:999px; display:inline-block; }
      .wpx-sim { font-weight:800; color:var(--ink); }
      .wpx-on { font-family:ui-monospace,Menlo,monospace; font-size:.74rem; color:var(--sub); white-space:nowrap; }
      .wpx-empty { text-align:center; color:#94a3b8; padding:42px 0; font-size:.9rem; }

      .wpx-lb { position:fixed; inset:0; z-index:2000; background:rgba(2,6,23,.82); display:flex; align-items:center;
        justify-content:center; padding:30px; cursor:zoom-out; animation: wpxFade .2s ease; }
      .wpx-lb img { max-width:90vw; max-height:88vh; border-radius:16px; box-shadow:0 30px 70px -20px rgba(0,0,0,.8); }
    `}</style>
  );

  if (loading) {
    return (
      <div className="wpx text-center py-5">
        {styleBlock}
        <Spinner role="status" animation="border" style={{ color: "var(--brand-primary)" }} />
        <p style={{ color: "var(--text-muted)", marginTop: "12px", fontWeight: 600 }}>Loading proctoring data…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="wpx">
        {styleBlock}
        <div className="wpx-card wpx-pad text-center">
          <p style={{ color: "var(--status-danger)", fontWeight: 600 }}>{error || "Failed to load proctoring data"}</p>
          <button className="wpx-hbtn wpx-hbtn-ghost" style={{ background: "#475569" }} onClick={onBack}>Back</button>
        </div>
      </div>
    );
  }

  const m = (key: string) => data.metrics.includes(key);
  const showSimilarity = m("similarityScore");
  const showZeroFace = m("zeroFace");

  const vcards = [
    { k: "Authenticate Face", v: data.violationSummary.authenticateFace, icon: "🧑‍💻", tint: "#6366f1", show: showSimilarity },
    { k: "Mobile", v: data.violationSummary.mobile, icon: "📱", tint: "#f59e0b", show: true },
    { k: "Second Face", v: data.violationSummary.secondFace, icon: "👥", tint: "#ec4899", show: true },
    { k: "Zero Face", v: data.violationSummary.zeroFace, icon: "🚫", tint: "#0ea5e9", show: showZeroFace },
  ].filter((c) => c.show);

  return (
    <div className="wpx">
      {styleBlock}

      {/* Notification modal */}
      <Modal show={info.show} onHide={() => setInfo({ ...info, show: false })} centered size="sm">
        <Modal.Body className="text-center" style={{ padding: "26px 20px", borderRadius: "14px" }}>
          <div style={{ fontSize: "1.8rem", marginBottom: "6px" }}>{info.variant === "success" ? "✅" : "⚠️"}</div>
          <p style={{ color: info.variant === "success" ? "#15803d" : "#b91c1c", fontWeight: 600, margin: "0 0 16px" }}>{info.message}</p>
          <Button size="sm" onClick={() => setInfo({ ...info, show: false })} style={{ backgroundColor: "#475569", color: "var(--text-on-brand)", border: "none", borderRadius: "9px", padding: "7px 22px", fontWeight: 700 }}>OK</Button>
        </Modal.Body>
      </Modal>

      {/* Photo lightbox */}
      {zoom && (
        <div className="wpx-lb" onClick={() => setZoom(null)}>
          <img src={zoom} alt="capture" />
        </div>
      )}

      {/* ── Hero ── */}
      <div className="wpx-hero">
        <div className="wpx-hero-row">
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div className="wpx-avatar-wrap">
              <div className="wpx-avatar-ring">
                {data.profilePhotoUrl ? (
                  <img className="wpx-avatar" src={data.profilePhotoUrl} alt="Profile" />
                ) : (
                  <div className="wpx-avatar">{data.studentName?.[0]?.toUpperCase() || "?"}</div>
                )}
              </div>
              <span className="wpx-live" />
            </div>
            <div>
              <div className="wpx-name">{data.studentName}</div>
              <div className="wpx-meta">
                <b>{data.studentCode || "—"}</b> &nbsp;·&nbsp; {data.testName} &nbsp;·&nbsp; {data.testDate}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
            <span className="wpx-id" title="Click to copy Tenant_TestId_UserId"
              onClick={() => { navigator.clipboard?.writeText(data.tenantTestIdUserId); notify(`Copied: ${data.tenantTestIdUserId}`, "success"); }}>
              &#128203; {data.tenantTestIdUserId}
            </span>
            <button className="wpx-hbtn wpx-hbtn-light" onClick={load}>↻ Refresh</button>
            <button className="wpx-hbtn wpx-hbtn-ghost" onClick={onBack}>← Back</button>
          </div>
        </div>
        <div className="wpx-hstats">
          <div className="wpx-hstat"><div className="n">{data.captures.length}</div><div className="l">Captures</div></div>
          <div className="wpx-hstat"><div className="n">{data.metrics.length}</div><div className="l">Metrics</div></div>
          <div className="wpx-hstat">
            <div className="n" style={{ color: blockTest ? "#fca5a5" : "#86efac" }}>{blockTest ? "Blocked" : "Active"}</div>
            <div className="l">Test Status</div>
          </div>
          <div className="wpx-hstat">
            <div className="n">{blanketFlag === "BLOCK" ? "Blocked" : blanketFlag === "ALLOW" ? "Allowed" : "Reset"}</div>
            <div className="l">Blanket</div>
          </div>
        </div>
      </div>

      {/* ── Block controls ── */}
      <div className="wpx-card wpx-pad" style={{ marginTop: "16px" }}>
        <div className="wpx-ctl">
          {/* Test Block — gated by JsonConfig.test.webProctoring.enableStatus===1;
              editable only for ROOT admins or the configured roleIds. */}
          {data.testBlockEnabled && (
            <div className="wpx-ctl-lbl">
              <span>🔒 Test Block</span>
              {data.testBlockPermission ? (
                <div className="wpx-seg" style={{ marginLeft: "4px" }}>
                  <button disabled={saving} data-on={blockTest ? "1" : "0"} onClick={() => handleTestBlock(true)}
                    style={blockTest ? { background: "linear-gradient(135deg,var(--status-danger),var(--status-danger))" } : {}}>Blocked</button>
                  <button disabled={saving} data-on={!blockTest ? "1" : "0"} onClick={() => handleTestBlock(false)}
                    style={!blockTest ? { background: "linear-gradient(135deg,var(--status-success),var(--status-success))" } : {}}>UnBlocked</button>
                </div>
              ) : (
                <span className="wpx-tag" style={{ marginLeft: "4px", background: blockTest ? "#fee2e2" : "#dcfce7", color: blockTest ? "#b91c1c" : "#15803d" }}>
                  <span className="wpx-dot" style={{ background: blockTest ? "#ef4444" : "#22c55e" }} />{blockTest ? "Blocked" : "UnBlocked"}
                </span>
              )}
            </div>
          )}

          {/* Blanket Block — admin only */}
          <div className="wpx-ctl-lbl">
            <span>🛡️ Blanket Block</span>
            {data.blanketBlockPermission ? (
              <div className="wpx-seg" style={{ marginLeft: "4px" }}>
                {(["BLOCK", "", "ALLOW"] as const).map((opt) => {
                  const labels: Record<string, string> = { BLOCK: "Blocked", "": "Reset", ALLOW: "Unblock" };
                  const grads: Record<string, string> = {
                    BLOCK: "linear-gradient(135deg,#ef4444,#dc2626)",
                    "": "linear-gradient(135deg,#0ea5e9,#0284c7)",
                    ALLOW: "linear-gradient(135deg,#22c55e,#16a34a)",
                  };
                  const active = blanketFlag === opt;
                  return (
                    <button key={opt || "reset"} disabled={saving} data-on={active ? "1" : "0"}
                      onClick={() => handleBlanket(opt)} style={active ? { background: grads[opt] } : {}}>
                      {labels[opt]}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span className="wpx-tag" style={{ marginLeft: "4px", background: "var(--surface-sunken)", color: "var(--text-body)" }}>
                {blanketFlag === "BLOCK" ? "Blocked" : blanketFlag === "ALLOW" ? "Unblock" : "Reset"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Audit Trail ── */}
      <div className="wpx-card wpx-pad" style={{ marginTop: "16px" }}>
        <div className="wpx-h">
          <span className="bar" /><span className="t">Audit Trail</span>
          {audit?.userActions?.length > 0 && <span className="c">{audit.userActions.length} events</span>}
        </div>
        {auditLoading ? (
          <div style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}><Spinner animation="border" size="sm" style={{ color: "var(--brand-primary)" }} /> Loading…</div>
        ) : audit?.error ? (
          <p style={{ color: "var(--status-danger)", fontSize: "0.82rem" }}>{audit.error}</p>
        ) : (
          <>
            <div className="wpx-chips">
              <span className="wpx-chip" style={{ background: "var(--brand-primary-subtle)", color: "var(--brand-primary-hover)", borderColor: "var(--brand-primary-subtle)" }}>💻 {audit?.deviceInfo?.os || "Unknown OS"}</span>
              <span className="wpx-chip" style={{ background: "#ecfeff", color: "#0e7490", borderColor: "#a5f3fc" }}>🌐 {audit?.deviceInfo?.browser || "Unknown browser"}</span>
              <span className="wpx-chip" style={{ background: "var(--status-warning-bg)", color: "var(--status-warning-text)", borderColor: "var(--status-warning-border)" }}>📡 {audit?.deviceInfo?.ip || "No IP"}</span>
            </div>
            {audit?.submitVerdict && (() => {
              const v = audit.submitVerdict;
              const p = v.type === "student"
                ? { bg: "var(--status-success-bg)", text: "var(--status-success-text)", border: "var(--status-success-border)", accent: "var(--status-success)", icon: "✔", tag: "STUDENT SUBMITTED" }
                : v.type === "staff"
                  ? { bg: "var(--status-warning-bg)", text: "var(--status-warning-text)", border: "var(--status-warning-border)", accent: "var(--status-warning)", icon: "⚠", tag: "STAFF POPULATED" }
                  : { bg: "var(--surface-muted)", text: "var(--text-muted)", border: "var(--border-default)", accent: "var(--text-faint)", icon: "—", tag: "NO SUBMISSION" };
              return (
                <div style={{ background: p.bg, border: `1px solid ${p.border}`, borderLeft: `4px solid ${p.accent}`, borderRadius: "8px", padding: "10px 14px", margin: "10px 0", display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "1.1rem", color: p.accent, flexShrink: 0 }}>{p.icon}</span>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                      <span style={{ padding: "2px 8px", borderRadius: "4px", background: p.accent, color: "var(--text-on-brand, #fff)", fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.4px" }}>{p.tag}</span>
                      <span style={{ fontWeight: 700, fontSize: "0.88rem", color: p.text }}>{v.title}</span>
                    </div>
                    <div style={{ fontSize: "0.8rem", color: p.text, lineHeight: 1.5 }}>{v.detail}</div>
                  </div>
                </div>
              );
            })()}
            {audit?.dynamoSubmitStatus && <div className="wpx-banner">{audit.dynamoSubmitStatus}</div>}
            {audit?.rdsSubmitStatus && <div className="wpx-banner rds">{audit.rdsSubmitStatus}</div>}
            {audit?.userActions?.length > 0 ? (
              <div className="wpx-tl" style={{ marginTop: "10px" }}>
                {audit.userActions.map((a: any, i: number) => (
                  <div className="wpx-tl-item" key={i}>
                    <span className="wpx-tl-dot" />
                    <span className="wpx-tl-act">{a.action}</span>
                    <span className="wpx-tl-ts">{a.timestamp}</span>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: "var(--text-faint)", fontSize: "0.85rem", padding: "6px 0" }}>No user actions recorded.</div>}
          </>
        )}
      </div>

      {/* ── Violation Summary ── */}
      <div className="wpx-card wpx-pad" style={{ marginTop: "16px" }}>
        <div className="wpx-h"><span className="bar" /><span className="t">Violation Summary</span></div>
        <div className="wpx-vs">
          {vcards.map((c) => {
            const muted = c.v === "--" || c.v == null || c.v === "";
            return (
              <div className="wpx-vcard" key={c.k}>
                <span className="edge" style={{ background: c.tint }} />
                <div className="ico" style={{ background: `${c.tint}1a`, color: c.tint }}>{c.icon}</div>
                <div className={`v${muted ? " muted" : ""}`}>{muted ? "—" : c.v}</div>
                <div className="k">{c.k}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Metrics ── */}
      <div className="wpx-card wpx-pad" style={{ marginTop: "16px", marginBottom: "10px" }}>
        <div className="wpx-h">
          <span className="bar" /><span className="t">Metrics</span>
          <span className="c">{data.captures.length} captures</span>
        </div>
        <div className="wpx-tbl-wrap">
          <table className="wpx-tbl">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Photo</th>
                {m("mobileCheck") && <th>Mobile Check</th>}
                {m("similarityScore") && <th>Similarity Score</th>}
                {m("numberOfFaces") && <th>Number of Faces</th>}
                {m("zeroFace") && <th>Zero Face</th>}
                {m("mood") && <th>Mood</th>}
                {m("moodConfidenceFactor") && <th>Mood Confidence Factor</th>}
                {m("createdOn") && <th>Created On</th>}
              </tr>
            </thead>
            <tbody>
              {data.captures.length === 0 ? (
                <tr><td colSpan={10}><div className="wpx-empty">🎞️ No proctoring captures found.</div></td></tr>
              ) : (
                data.captures.map((c) => {
                  const pf = passFail(c.mobileCheck);
                  return (
                    <tr key={c.sno}>
                      <td><span className="wpx-sno">{c.sno}</span></td>
                      <td>{c.photoUrl ? <img className="wpx-photo" src={c.photoUrl} alt={`capture-${c.sno}`} onClick={() => setZoom(c.photoUrl)} /> : "—"}</td>
                      {m("mobileCheck") && <td><span className="wpx-mchip" style={{ background: pf.bg, color: pf.fg, border: `1px solid ${pf.bd}` }}>{c.mobileCheck}</span></td>}
                      {m("similarityScore") && <td><span className="wpx-sim">{c.similarity}</span></td>}
                      {m("numberOfFaces") && <td>{c.faces}</td>}
                      {m("zeroFace") && <td>{c.zeroFaces}</td>}
                      {m("mood") && <td>{c.mood}</td>}
                      {m("moodConfidenceFactor") && <td>{c.moodConfidenceFactor}</td>}
                      {m("createdOn") && <td><span className="wpx-on">{c.createdOn}</span></td>}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WebProctoringPanel;
